// Duplicate content detection and canonical URL management
import { supabase } from '@/integrations/supabase/client';

export interface DuplicateContentIssue {
  url1: string;
  url2: string;
  similarity: number;
  issue: 'duplicate' | 'near-duplicate' | 'canonical-missing';
  recommendation: string;
}

export interface CanonicalRule {
  pattern: string;
  canonicalTemplate: string;
  priority: number;
}

export class DuplicateContentManager {
  private baseUrl = 'https://www.carolinatrophyroofs.com';

  // Canonical URL rules for different page types
  private canonicalRules: CanonicalRule[] = [
    {
      pattern: '/locations/(.+)',
      canonicalTemplate: '/locations/{1}',
      priority: 100
    },
    {
      pattern: '/blog/(.+)',
      canonicalTemplate: '/blog/{1}',
      priority: 100
    },
    {
      pattern: '/services/(.+)',
      canonicalTemplate: '/services/{1}',
      priority: 100
    },
    {
      pattern: '/(.+)\\?.*',
      canonicalTemplate: '/{1}',
      priority: 90
    },
    {
      pattern: '/(.+)/$',
      canonicalTemplate: '/{1}',
      priority: 80
    }
  ];

  // Generate canonical URL for any given URL
  generateCanonicalUrl(url: string): string {
    // Ensure www subdomain is used
    let normalizedUrl = url;
    
    // Replace http with https
    normalizedUrl = normalizedUrl.replace(/^http:/, 'https:');
    
    // Ensure www subdomain exists
    normalizedUrl = normalizedUrl.replace(/^https:\/\/carolinatrophyroofs\.com/, 'https://www.carolinatrophyroofs.com');
    
    // Remove base URL if present to get the path
    const path = normalizedUrl.replace(this.baseUrl, '').replace(/^https?:\/\/[^/]+/, '');
    
    // Apply canonical rules to normalize the path
    for (const rule of this.canonicalRules.sort((a, b) => b.priority - a.priority)) {
      const regex = new RegExp(rule.pattern);
      const match = path.match(regex);
      
      if (match) {
        let canonical = rule.canonicalTemplate;
        // Replace placeholders with matched groups
        match.slice(1).forEach((group, index) => {
          canonical = canonical.replace(`{${index + 1}}`, group);
        });
        return this.baseUrl + canonical;
      }
    }
    
    // Default: return clean URL with www
    const cleanPath = path.split('?')[0].replace(/\/$/, '') || '';
    return this.baseUrl + cleanPath;
  }

  // Detect duplicate content across the site
  async detectDuplicateContent(): Promise<DuplicateContentIssue[]> {
    const issues: DuplicateContentIssue[] = [];

    try {
      // Check blog posts for similar content
      const { data: posts } = await supabase
        .from('blog_posts')
        .select('slug, title, excerpt, content')
        .eq('status', 'published');

      if (posts) {
        // Compare posts for similarity
        for (let i = 0; i < posts.length; i++) {
          for (let j = i + 1; j < posts.length; j++) {
            const similarity = this.calculateContentSimilarity(
              posts[i].content,
              posts[j].content
            );

            if (similarity > 0.8) {
              issues.push({
                url1: `${this.baseUrl}/blog/${posts[i].slug}`,
                url2: `${this.baseUrl}/blog/${posts[j].slug}`,
                similarity,
                issue: 'duplicate',
                recommendation: 'Consolidate or significantly differentiate content'
              });
            } else if (similarity > 0.6) {
              issues.push({
                url1: `${this.baseUrl}/blog/${posts[i].slug}`,
                url2: `${this.baseUrl}/blog/${posts[j].slug}`,
                similarity,
                issue: 'near-duplicate',
                recommendation: 'Add more unique content to differentiate pages'
              });
            }
          }
        }
      }

      // Check location pages for template duplication
      const { data: locations } = await supabase
        .from('service_locations')
        .select('name, historical_fact');

      if (locations) {
        const shortDescriptions = locations.filter(loc => 
          !loc.historical_fact || loc.historical_fact.length < 100
        );

        shortDescriptions.forEach(location => {
          issues.push({
            url1: `${this.baseUrl}/locations/${location.name.toLowerCase().replace(/\s+/g, '-')}`,
            url2: '',
            similarity: 0.9,
            issue: 'duplicate',
            recommendation: 'Add more unique, location-specific content (>300 words)'
          });
        });
      }

    } catch (error) {
      console.error('Error detecting duplicate content:', error);
    }

    return issues;
  }

  // Calculate content similarity using simple algorithm
  private calculateContentSimilarity(content1: string, content2: string): number {
    // Remove HTML tags and normalize
    const clean1 = this.cleanContent(content1);
    const clean2 = this.cleanContent(content2);

    // Calculate word overlap
    const words1 = new Set(clean1.toLowerCase().split(/\s+/));
    const words2 = new Set(clean2.toLowerCase().split(/\s+/));
    
    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);
    
    return intersection.size / union.size;
  }

  private cleanContent(content: string): string {
    return content
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  }

  // Generate redirect rules for duplicate URLs
  generateRedirectRules(): Array<{ from: string; to: string; type: number }> {
    return [
      // Remove trailing slashes
      { from: '/(.+)/$', to: '/$1', type: 301 },
      
      // Remove query parameters (except UTM)
      { from: '/(.+)\\?(?!utm_)', to: '/$1', type: 301 },
      
      // Redirect old URL patterns
      { from: '/location/(.+)', to: '/locations/$1', type: 301 },
      { from: '/service/(.+)', to: '/services/$1', type: 301 },
      
      // Redirect common duplicates
      { from: '/home', to: '/', type: 301 },
      { from: '/index', to: '/', type: 301 },
      { from: '/index.html', to: '/', type: 301 },
      
      // Case sensitivity redirects
      { from: '/Blog/(.+)', to: '/blog/$1', type: 301 },
      { from: '/Locations/(.+)', to: '/locations/$1', type: 301 },
      { from: '/Services/(.+)', to: '/services/$1', type: 301 }
    ];
  }

  // Check if page needs canonical tag
  needsCanonicalTag(currentUrl: string): {
    needs: boolean;
    canonical: string;
    reason?: string;
  } {
    const canonical = this.generateCanonicalUrl(currentUrl);
    
    if (canonical !== currentUrl) {
      return {
        needs: true,
        canonical,
        reason: 'URL normalization required'
      };
    }

    // Check for common duplicate patterns
    if (currentUrl.includes('?') && !currentUrl.includes('utm_')) {
      return {
        needs: true,
        canonical: currentUrl.split('?')[0],
        reason: 'Remove query parameters'
      };
    }

    if (currentUrl.endsWith('/') && currentUrl !== this.baseUrl + '/') {
      return {
        needs: true,
        canonical: currentUrl.slice(0, -1),
        reason: 'Remove trailing slash'
      };
    }

    return {
      needs: false,
      canonical: currentUrl
    };
  }

  // Generate hreflang tags for international content
  generateHreflangTags(currentUrl: string): Array<{ hreflang: string; href: string }> {
    // For now, just US English
    return [
      { hreflang: 'en-US', href: currentUrl },
      { hreflang: 'x-default', href: currentUrl }
    ];
  }

  // Audit content for SEO issues
  async auditContentQuality(content: string): Promise<{
    wordCount: number;
    uniqueness: number;
    readability: number;
    keywordDensity: { [key: string]: number };
    issues: string[];
    recommendations: string[];
  }> {
    const cleanContent = this.cleanContent(content);
    const words = cleanContent.split(/\s+/).filter(word => word.length > 2);
    
    // Calculate keyword density
    const keywordCounts: { [key: string]: number } = {};
    words.forEach(word => {
      const cleaned = word.toLowerCase().replace(/[^a-z]/g, '');
      keywordCounts[cleaned] = (keywordCounts[cleaned] || 0) + 1;
    });

    const keywordDensity: { [key: string]: number } = {};
    Object.entries(keywordCounts).forEach(([word, count]) => {
      keywordDensity[word] = (count / words.length) * 100;
    });

    // Identify issues
    const issues: string[] = [];
    const recommendations: string[] = [];

    if (words.length < 300) {
      issues.push('Content too short (less than 300 words)');
      recommendations.push('Add more detailed, valuable content');
    }

    const topKeywords = Object.entries(keywordDensity)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    topKeywords.forEach(([word, density]) => {
      if (density > 3) {
        issues.push(`Keyword "${word}" density too high (${density.toFixed(1)}%)`);
        recommendations.push(`Reduce usage of "${word}" or add more content`);
      }
    });

    return {
      wordCount: words.length,
      uniqueness: 0.85, // Placeholder - would need external API for real uniqueness check
      readability: this.calculateReadabilityScore(cleanContent),
      keywordDensity,
      issues,
      recommendations
    };
  }

  private calculateReadabilityScore(content: string): number {
    // Simple Flesch Reading Ease approximation
    const sentences = content.split(/[.!?]+/).length;
    const words = content.split(/\s+/).length;
    const syllables = this.countSyllables(content);

    if (sentences === 0 || words === 0) return 0;

    const score = 206.835 - (1.015 * (words / sentences)) - (84.6 * (syllables / words));
    return Math.max(0, Math.min(100, score));
  }

  private countSyllables(text: string): number {
    // Simple syllable counting
    return text.toLowerCase()
      .replace(/[^a-z]/g, '')
      .replace(/[aeiou]{2,}/g, 'a')
      .replace(/[^aeiou]/g, '')
      .length;
  }
}

export const duplicateContentManager = new DuplicateContentManager();